import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MarkdownViewer } from "@/components/markdown-viewer";
import { SkillList, type SkillListEntry } from "@/components/skill-list";
import { Spinner } from "@/components/ui/spinner";
import { useRefreshOnReconnect } from "@/hooks/use-backend-connectivity";
import { useSkillWiring } from "@/hooks/use-skill-wiring";
import {
  type WorkspaceSkillWiringError,
  useWorkspaceSkillWiring,
} from "@/hooks/use-workspace-skill-wiring";
import { cn, fetchWithAuth } from "@/lib/utils";

interface AgentPlaybook {
  /** A display name supplied by a newer capabilities endpoint. */
  name?: string | null;
  /** Raw Markdown supplied by a newer capabilities endpoint. */
  content?: string | null;
  /** Accepted as a compatibility alias for raw Markdown. */
  markdown?: string | null;
}

interface AgentCapabilities {
  id: string;
  name: string;
  description: string | null;
  /** Optional so this UI can be deployed before the server exposes it. */
  model?: string | null;
  /** Compatibility alias used by some model-list endpoints. */
  model_slug?: string | null;
  /** Optional effective access roles from a newer capabilities endpoint. */
  allowed_roles?: string[] | null;
  /** Compatibility alias for allowed_roles. */
  roles?: string[] | null;
  /** Optional raw playbook Markdown from a newer capabilities endpoint. */
  playbooks?: AgentPlaybook[] | null;
  delegation: {
    description: string | null;
    when_to_use: string | null;
    when_not_to_use: string | null;
    example_questions: string[];
  } | null;
  skills: Array<{ name: string; description: string }>;
  tools: Array<{ name: string; label: string; enabled: boolean; description: string }>;
  sub_agents: Array<{ id: string; needs_approval: boolean }>;
}

interface AgentAboutSummary {
  name?: string;
  description?: string | null;
  model?: string | null;
  roles?: string[] | null;
  allowed_roles?: string[] | null;
}

interface AgentAboutDialogProps {
  agentId: string | null;
  /** Optional directory metadata. Older agent-list responses omit it. */
  agent?: AgentAboutSummary;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  getToken: () => Promise<string>;
}

interface DisplayPlaybook {
  name: string | null;
  content: string;
}

const COLLAPSIBLE_MARKDOWN_LENGTH = 480;

function nonEmptyText(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function modelLabel(value: unknown): string | null {
  const direct = nonEmptyText(value);
  if (direct) return direct;
  if (!value || typeof value !== "object") return null;

  const model = value as Record<string, unknown>;
  return (
    nonEmptyText(model.slug)
    ?? nonEmptyText(model.id)
    ?? nonEmptyText(model.name)
  );
}

function workspaceSkillErrorMessage(
  t: (key: string) => string,
  error: WorkspaceSkillWiringError,
): string {
  switch (error) {
    case "authentication":
      return t("connections:workspaceSkills.errorAuthentication");
    case "forbidden":
      return t("connections:workspaceSkills.errorForbidden");
    case "skill_changed":
      return t("connections:workspaceSkills.errorSkillChanged");
    case "not_found":
      return t("connections:workspaceSkills.errorNotFound");
    case "unavailable":
      return t("connections:workspaceSkills.errorUnavailable");
    case "invalid_response":
      return t("connections:workspaceSkills.errorInvalidResponse");
    case "network":
      return t("connections:workspaceSkills.errorNetwork");
    default:
      return t("connections:workspaceSkills.errorInvalidRequest");
  }
}

/**
 * Keep an explicit empty list distinct from an absent field: an endpoint can
 * say that no roles are configured, while an older endpoint says nothing.
 */
function stringList(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;

  const values: string[] = [];
  for (const item of value) {
    const text = nonEmptyText(item);
    if (text) values.push(text);
  }
  return [...new Set(values)];
}

function capabilityRoles(data: AgentCapabilities | null): string[] | null {
  return stringList(data?.allowed_roles) ?? stringList(data?.roles);
}

function displayPlaybooks(value: unknown): DisplayPlaybook[] {
  if (!Array.isArray(value)) return [];

  return value.flatMap((entry) => {
    if (!entry || typeof entry !== "object") return [];
    const playbook = entry as Record<string, unknown>;
    const content = nonEmptyText(playbook.content) ?? nonEmptyText(playbook.markdown);
    if (!content) return [];
    return [{ name: nonEmptyText(playbook.name), content }];
  });
}

function ExpandableMarkdown({
  content,
}: {
  content: string;
}) {
  const { t } = useTranslation();
  const contentId = useId();
  const contentRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(
    content.length > COLLAPSIBLE_MARKDOWN_LENGTH,
  );

  const measureOverflow = useCallback(() => {
    const element = contentRef.current;
    if (!element) return;
    setHasOverflow((current) => (
      current || element.scrollHeight > element.clientHeight + 1
    ));
  }, []);

  useEffect(() => {
    setExpanded(false);
    setHasOverflow(content.length > COLLAPSIBLE_MARKDOWN_LENGTH);

    const element = contentRef.current;
    if (!element) return;
    const observer = typeof ResizeObserver === "undefined"
      ? null
      : new ResizeObserver(measureOverflow);
    observer?.observe(element);
    const timeout = window.setTimeout(measureOverflow, 0);

    return () => {
      observer?.disconnect();
      window.clearTimeout(timeout);
    };
  }, [content, measureOverflow]);

  return (
    <div>
      <div
        id={contentId}
        ref={contentRef}
        className={cn("relative min-w-0", !expanded && "max-h-40 overflow-hidden")}
      >
        <MarkdownViewer
          content={content}
          className="text-sm"
          onMarkdownLoaded={measureOverflow}
        />
        {!expanded && hasOverflow && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-muted/90 via-muted/60 to-transparent"
          />
        )}
      </div>
      {hasOverflow && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="mt-1 h-7 px-2 text-xs"
          aria-controls={contentId}
          aria-expanded={expanded}
          onClick={() => setExpanded((current) => !current)}
        >
          {expanded ? t("agent.showLess") : t("agent.showMore")}
        </Button>
      )}
    </div>
  );
}

export function AgentAboutDialog({
  agentId,
  agent,
  open,
  onOpenChange,
  getToken,
}: AgentAboutDialogProps) {
  const { t } = useTranslation();
  const [data, setData] = useState<AgentCapabilities | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestVersion = useRef(0);
  const skillWiring = useSkillWiring(getToken, open && Boolean(agentId));
  const workspaceSkillAgentId = open ? nonEmptyText(data?.id) ?? "" : "";
  const workspaceSkillWiring = useWorkspaceSkillWiring(
    workspaceSkillAgentId,
    getToken,
  );

  const loadCapabilities = useCallback(async () => {
    if (!agentId) return;

    const version = ++requestVersion.current;
    setLoading(true);
    setError(null);
    setData(null);

    try {
      const response = await fetchWithAuth(
        `/api/agents/${encodeURIComponent(agentId)}/capabilities`,
        { cache: "no-store" },
        getToken,
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const capabilities = (await response.json()) as AgentCapabilities;
      if (requestVersion.current === version) setData(capabilities);
    } catch (loadError) {
      if (requestVersion.current === version) {
        setError(loadError instanceof Error ? loadError.message : t("common:status.unknownError"));
      }
    } finally {
      if (requestVersion.current === version) setLoading(false);
    }
  }, [agentId, getToken]);

  useRefreshOnReconnect(loadCapabilities, open && Boolean(agentId));

  useEffect(() => {
    if (!open || !agentId) return;
    void loadCapabilities();
    return () => {
      requestVersion.current += 1;
    };
  }, [agentId, loadCapabilities, open]);

  const delegation = data?.delegation;
  const delegationQuestions = Array.isArray(delegation?.example_questions)
    ? delegation.example_questions
    : [];
  const skills = Array.isArray(data?.skills) ? data.skills : [];
  const wiringBySkill = new Map(
    (skillWiring.data?.skills ?? []).map((summary) => [summary.skill_id, summary]),
  );
  const workspaceSkills = workspaceSkillAgentId && !workspaceSkillWiring.unsupported
    ? workspaceSkillWiring.data?.skills ?? []
    : [];
  const skillDescriptionsByName = new Map(
    skills.map((skill) => [skill.name, skill.description]),
  );
  const workspaceSkillEntries: SkillListEntry[] = workspaceSkills.map((skill, index) => {
    // The list identity is display-safe and distinct from the signed handle.
    // Opaque workspace and requirement handles remain only in callback
    // closures and browser request state; they are never rendered into the DOM.
    const entryId = `workspace-skill-${index}-${skill.name}`;
    const requirementsByEnv = new Map(
      skill.requirements.map((requirement) => [requirement.env, requirement]),
    );

    return {
      id: entryId,
      name: skill.name,
      description: skill.description ?? skillDescriptionsByName.get(skill.name),
      wiring: {
        skill_id: entryId,
        description: skill.description ?? skillDescriptionsByName.get(skill.name),
        // Workspace wiring has no executable-script breakdown, but an empty
        // array deliberately retains the shared skill-level credentials drawer.
        scripts: [],
        // This client-constructed wiring is known to be an ordinary credential
        // form; explicit null avoids treating it like an older server catalog.
        connection: null,
        status: skill.status,
        required: skill.required,
        resolved: skill.resolved,
        requirements: skill.requirements.map((requirement) => ({
          env: requirement.env,
          status: requirement.status,
          source: requirement.source,
        })),
      },
      browserTarget: {
        source: "workspace",
        agentId: workspaceSkillAgentId,
        workspaceSkillId: skill.workspace_skill_id,
      },
      wiringLoading: workspaceSkillWiring.loading,
      // Workspace and catalog wiring fail independently; do not let a global
      // catalog failure replace this entry's value-free status.
      wiringError: null,
      onRefresh: workspaceSkillWiring.refresh,
      detailsDisabled: workspaceSkillWiring.loading || Boolean(workspaceSkillWiring.error),
      saveSkillSecret: async (_entryId, environment, value, scope) => {
        const requirement = requirementsByEnv.get(environment);
        if (!requirement) return false;
        return workspaceSkillWiring.saveWorkspaceSkillSecret(
          skill.workspace_skill_id,
          requirement.requirement_id,
          value,
          scope,
        );
      },
      deleteSkillSecret: async (_entryId, environment, scope) => {
        const requirement = requirementsByEnv.get(environment);
        if (!requirement) return false;
        return workspaceSkillWiring.deleteWorkspaceSkillSecret(
          skill.workspace_skill_id,
          requirement.requirement_id,
          scope,
        );
      },
    };
  });
  const allSkillEntries: SkillListEntry[] = [
    ...skills.map((skill) => ({
      id: skill.name,
      name: skill.name,
      description: skill.description,
      wiring: wiringBySkill.get(skill.name),
    })),
    ...workspaceSkillEntries,
  ];
  const workspaceSkillError = workspaceSkillAgentId && !workspaceSkillWiring.unsupported
    ? workspaceSkillWiring.error
    : null;
  const shouldShowSkills = allSkillEntries.length > 0 || Boolean(workspaceSkillError);
  const tools = Array.isArray(data?.tools) ? data.tools : [];
  const subAgents = Array.isArray(data?.sub_agents) ? data.sub_agents : [];
  const description = nonEmptyText(data?.description) ?? nonEmptyText(agent?.description);
  const model = modelLabel(data?.model) ?? modelLabel(data?.model_slug) ?? modelLabel(agent?.model);
  const roles = capabilityRoles(data)
    ?? stringList(agent?.roles)
    ?? stringList(agent?.allowed_roles)
    ?? [];
  const playbooks = displayPlaybooks(data?.playbooks);
  const title = data?.name ?? agent?.name ?? agentId;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="sr-only">
            {t("agent.aboutAgent", { name: title ?? "" })}
          </DialogDescription>
          {(model || roles.length > 0) && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-1 text-sm">
              {model && (
                <div className="flex min-w-0 items-center gap-2">
                  <span className="font-medium text-muted-foreground">{t("agent.model")}</span>
                  <Badge variant="secondary" className="max-w-full break-all">
                    {model}
                  </Badge>
                </div>
              )}
              {roles.length > 0 && (
                <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                  <span className="mr-1 font-medium text-muted-foreground">{t("agent.roles")}</span>
                  {roles.map((role) => (
                    <Badge key={role} variant="outline">{role}</Badge>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogHeader>

        {loading && (
          <div className="flex min-h-32 flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
            <Spinner className="size-5" />
            <span>{t("agent.loadingCapabilities")}</span>
          </div>
        )}

        {error && !loading && (
          <div className="flex items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
            <span className="text-destructive">{t("agent.errorLoading")}</span>
            <Button size="sm" variant="outline" onClick={() => void loadCapabilities()}>
              {t("agent.retry")}
            </Button>
          </div>
        )}

        {data && !loading && !error && (
          <div className="space-y-5">
            {description && (
              <section className="rounded-md border bg-muted/50 p-3">
                <h3 className="mb-2 text-sm font-semibold">{t("agent.description")}</h3>
                <ExpandableMarkdown content={description} />
              </section>
            )}

            {playbooks.length > 0 && (
              <section className="space-y-3 rounded-md border bg-muted/50 p-3">
                <h3 className="text-sm font-semibold">{t("agent.playbooks")}</h3>
                {playbooks.map((playbook, index) => (
                  <div key={`${playbook.name ?? "playbook"}-${index}`}>
                    {playbook.name && (
                      <h4 className="mb-2 text-sm font-medium">{playbook.name}</h4>
                    )}
                    <ExpandableMarkdown content={playbook.content} />
                  </div>
                ))}
              </section>
            )}

            {delegation && (
              <section className="rounded-md border bg-muted/50 p-3">
                <h3 className="mb-2 text-sm font-semibold">{t("agent.delegationGuide")}</h3>
                {delegation.description && (
                  <p className="mb-3 text-sm text-muted-foreground">{delegation.description}</p>
                )}
                {delegation.when_to_use && (
                  <div className="mb-3">
                    <p className="text-xs font-medium">{t("agent.whenToUse")}:</p>
                    <p className="text-sm text-muted-foreground">{delegation.when_to_use}</p>
                  </div>
                )}
                {delegation.when_not_to_use && (
                  <div className="mb-3">
                    <p className="text-xs font-medium">{t("agent.whenNotToUse")}:</p>
                    <p className="text-sm text-muted-foreground">{delegation.when_not_to_use}</p>
                  </div>
                )}
                {delegationQuestions.length > 0 && (
                  <div>
                    <p className="text-xs font-medium">{t("agent.exampleQuestions")}:</p>
                    <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                      {delegationQuestions.map((question, index) => (
                        <li key={`${question}-${index}`}>{question}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </section>
            )}

            {shouldShowSkills && (
              <section>
                <h3 className="text-sm font-semibold mb-2">{t("agent.skills")}</h3>
                {skillWiring.error && (
                  <p className="mb-2 flex items-center gap-2 text-xs text-muted-foreground" role="status">
                    {t("connections:skills.wiringUnavailable")}
                  </p>
                )}
                {workspaceSkillError && (
                  <Alert
                    variant="destructive"
                    className="mb-2 flex flex-wrap items-center justify-between gap-3"
                    data-testid="workspace-skill-wiring-error"
                  >
                    <AlertDescription>
                      {workspaceSkillErrorMessage(t, workspaceSkillError)}
                    </AlertDescription>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => void workspaceSkillWiring.refresh()}
                    >
                      {t("common:actions.retry")}
                    </Button>
                  </Alert>
                )}
                <SkillList
                  className="overflow-hidden rounded-md border"
                  skills={allSkillEntries}
                  getToken={getToken}
                  wiringLoading={skillWiring.loading}
                  wiringError={skillWiring.error}
                  onRefresh={skillWiring.refresh}
                  saveSkillSecret={skillWiring.saveSkillSecret}
                  deleteSkillSecret={skillWiring.deleteSkillSecret}
                  ariaLabel={t("agent.skills")}
                />
              </section>
            )}

            {tools.length > 0 && (
              <section>
                <h3 className="text-sm font-semibold mb-2">{t("agent.tools")}</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-muted-foreground text-xs uppercase tracking-wide">
                        <th className="pb-2 pr-4 font-medium">{t("agent.tools")}</th>
                        <th className="pb-2 font-medium">{t("agent.description")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tools.map((tool) => (
                        <tr key={tool.name} className="border-t">
                          <td className="py-2 pr-4 align-top font-medium">{tool.label}</td>
                          <td className="py-2 text-muted-foreground">{tool.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {subAgents.length > 0 && (
              <section>
                <h3 className="text-sm font-semibold mb-2">{t("agent.canDelegate")}</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-muted-foreground text-xs uppercase tracking-wide">
                        <th className="pb-2 pr-4 font-medium">{t("agent.agent")}</th>
                        <th className="pb-2 font-medium">{t("agent.requiresApproval")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subAgents.map((subAgent) => (
                        <tr key={subAgent.id} className="border-t">
                          <td className="py-2 pr-4 align-top font-medium">{subAgent.id}</td>
                          <td className="py-2">
                            {subAgent.needs_approval ? (
                              <Badge variant="secondary">{t("agent.requiresApproval")}</Badge>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
